import { getTenantBasePath } from '@low6-pickem/frontend-shared';
import type { LobbyGame } from '../api/getLobby';

/** Origin of an absolute URL, or null when it is relative or unparseable. */
function originOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * Which host serves the Squares app.
 *
 * The contest's OWN public URL is asked first and the lobby listing is only a fallback. Searching
 * the listing for "any squares contest" was the only source before, and it fails exactly when a
 * player most needs the link: a contest that has finished, or is hidden from the lobby, is absent
 * from the listing, so My Games had no squares host to borrow and fell back to a RELATIVE `/play`.
 * Relative resolves against the lobby's own origin, so the player landed on the lobby's
 * "That URL doesn't match a lobby route" page instead of their game (DKPLAT, 22 Sep 2026).
 *
 * Returns null rather than a relative path when no host can be determined: a missing link is
 * honest, a link into the lobby's 404 is not.
 */
export function getSquaresPlayOrigin(
  lobbyGames: LobbyGame[],
  contestPublicUrl?: string | null
): string | null {
  return (
    originOf(contestPublicUrl) ??
    originOf(lobbyGames.find(g => g.game_type === 'squares')?.public_url)
  );
}

/** Base URL for Squares play view: {squaresOrigin}{tenantPath}/play — null when the host is unknown. */
export function getSquaresPlayBase(
  lobbyGames: LobbyGame[],
  contestPublicUrl?: string | null
): string | null {
  const origin = getSquaresPlayOrigin(lobbyGames, contestPublicUrl);
  if (!origin) return null;
  const tenantPath = (getTenantBasePath() ?? '').replace(/\/$/, '');
  return `${origin}${tenantPath}/play`;
}

/**
 * Raw play URL for a Squares contest (before token / from=lobby).
 *
 * `contestPublicUrl` is that contest's own public URL when the caller has one; it fixes both the
 * host and the last-resort link. Falls back to that URL whole when the play base cannot be built,
 * so the player still reaches the contest (via the game's own entry route) rather than nowhere.
 */
export function buildSquaresContestPlayUrl(
  contestId: string,
  lobbyGames: LobbyGame[],
  contestPublicUrl?: string | null
): string | null {
  const base = getSquaresPlayBase(lobbyGames, contestPublicUrl);
  if (!base) return contestPublicUrl ?? null;
  return `${base}?contestId=${encodeURIComponent(contestId)}`;
}

/**
 * Resolve the raw play URL for a lobby game.
 * Squares `public_url` points at `/game/{questionId}`; the play view expects
 * `/play?contestId={pickemId}` (param name stays contestId, value is pickem id).
 */
export function resolveSquaresLobbyGameRawPlayUrl(
  game: LobbyGame,
  lobbyGames: LobbyGame[]
): string | null {
  if (game.game_type !== 'squares') {
    return game.public_url;
  }
  // The tile's own public_url, not a search of the listing: a section can render a contest the
  // top-level `games` array never carries, and then the search finds nothing.
  return buildSquaresContestPlayUrl(game.pickem_id, lobbyGames, game.public_url);
}
